import React, { useState } from 'react'
import Sidebar from '@/components/ui/Sidebar/Sidebar'
import { FormContainer, FormItem } from '@/components/ui/Form'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { apiService } from '@/utils/api'

interface InviteUserSidebarProps {
  isOpen: boolean
  onClose: () => void
  deviceId: string
  currentUserEmail?: string
  onSendInvite?: (payload: {
    deviceId: string
    email: string
    permission: { fullAccess: boolean; readOnly: boolean; ownerShip: boolean }
  }) => Promise<void> | void
}

const InviteUserSidebar: React.FC<InviteUserSidebarProps> = ({
  isOpen,
  onClose,
  deviceId,
  currentUserEmail,
  onSendInvite,
}) => {
  const [inviteEmail, setInviteEmail] = useState('')
  const [permissionMode, setPermissionMode] = useState<'full' | 'owner'>('full')
  const [makeOwner, setMakeOwner] = useState(false)

  const validateEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  const handleSend = async () => {
    if (!deviceId || !validateEmail(inviteEmail)) return

    try {
      if (permissionMode === 'full') {
        await apiService.post<{ invitationID: string }>(
          `/api/v1/iot-shadow/device/${deviceId}/createInvitation`,
          {
            device_id: deviceId,
            email_address: inviteEmail,
            access: 'full',
          }
        )
      } else {
        await apiService.post<{ invitationID: string }>(
          `/api/v1/iot-shadow/device/${deviceId}/createOwnerInvitation`,
          {
            device_id: deviceId,
            email_address: inviteEmail,
            access: 'owner',
          }
        )
      }

      // Optional callback for parent to refresh or close
      await onSendInvite?.({
        deviceId,
        email: inviteEmail,
        permission: {
          fullAccess: permissionMode === 'full',
          readOnly: false,
          ownerShip: permissionMode === 'owner',
        },
      })
    } finally {
      setInviteEmail('')
      setPermissionMode('full')
    }
  }

  const isSelfInvite = currentUserEmail && inviteEmail && inviteEmail.toLowerCase() === currentUserEmail.toLowerCase()

  return (
    <Sidebar isOpen={isOpen} onClose={onClose} title="Create Invitation">
      <div className="space-y-4">
        <div className="text-sm text-gray-600">
          Device: <span className="font-medium">{deviceId || '-'}</span>
        </div>
        <FormContainer>
          <FormItem label="Invitee Email">
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInviteEmail(e.target.value)}
              placeholder="Enter email"
            />
            {inviteEmail && !validateEmail(inviteEmail) && (
              <div className="mt-1 text-xs text-red-500">Please input valid email address</div>
            )}
            {isSelfInvite && (
              <div className="mt-1 text-xs text-yellow-600">You cannot invite yourself</div>
            )}
          </FormItem>
          <FormItem label="Permission">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={`px-2 py-1 rounded border ${permissionMode === 'full' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 hover:bg-gray-50'}`}
                onClick={() => setPermissionMode('full')}
              >
                Full Access
              </button>
              <button
                type="button"
                className={`px-2 py-1 rounded border ${permissionMode === 'owner' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 hover:bg-gray-50'}`}
                onClick={() => setPermissionMode('owner')}
              >
                Hand Over Ownership
              </button>
            </div>
          </FormItem>
          <FormItem>
            <div className="flex items-center justify-end gap-2">
              <Button
                disabled={!deviceId || !validateEmail(inviteEmail) || Boolean(isSelfInvite)}
                onClick={handleSend}
                variant="solid"
              >
                Send Invitation
              </Button>
              <Button variant="default" onClick={onClose}>Cancel</Button>
            </div>
          </FormItem>
        </FormContainer>
      </div>
    </Sidebar>
  )
}

export default InviteUserSidebar